// api.ts — Thin client for the farstars session API. The SPA loads its galaxy
// from the SSO-gated /api/session; on 401 it bounces to the OIDC login, and on
// any network failure (local dev with no server) the caller falls back to
// generating a galaxy client-side.
import type { Galaxy } from './types';
import type { Command, SimState } from './state/economy';

export interface Player {
  email: string;
  name: string;
}

export interface Session {
  player: Player;
  galaxy: Galaxy;
}

/** Handle on the live /api/state WebSocket: push speed + focus changes, tear it down. */
export interface SimController {
  setSpeed(speed: number): void;
  /** Tell the server which system we're viewing, so it streams that system's economy
   *  (the server simulates the whole galaxy; null = galaxy view, ships only). */
  setFocus(systemId: string | null): void;
  /** Send a player order (matches only); resolves with the server's verdict. */
  command(cmd: Command): Promise<CommandResult>;
  close(): void;
}

export interface CommandResult {
  ok: boolean;
  error?: string;
}

// AuthRequiredError signals the session API returned 401 — the caller should
// redirect to the OIDC login.
export class AuthRequiredError extends Error {}

// fetchMe checks the caller is signed in (401 → AuthRequiredError) without touching any
// galaxy — the lobby needs an identity, not a sandbox galaxy.
export async function fetchMe(): Promise<Player> {
  const res = await fetch(withDevUser('/api/me'), { headers: { Accept: 'application/json' } });
  if (res.status === 401) throw new AuthRequiredError('authentication required');
  if (!res.ok) throw new Error(`me request failed: ${res.status}`);
  return (await res.json()) as Player;
}

export async function fetchSession(): Promise<Session> {
  const res = await fetch('/api/session', { headers: { Accept: 'application/json' } });
  if (res.status === 401) {
    throw new AuthRequiredError('authentication required');
  }
  if (!res.ok) {
    throw new Error(`session request failed: ${res.status}`);
  }
  return (await res.json()) as Session;
}

// loginRedirect sends the browser through the authorization-code flow. With a
// provider hint it skips authz's own login page and lands on Google/GitHub
// directly — those consent screens can't be embedded, so this stays a
// redirect even when password sign-in happens in-page.
export function loginRedirect(idpHint?: string): void {
  const returnTo = window.location.pathname + window.location.search;
  const q = new URLSearchParams({ return_to: returnTo });
  if (idpHint) q.set('idp_hint', idpHint);
  window.location.href = `/api/oidc/login?${q.toString()}`;
}

// LoginCapabilities is what the backend says about signing in here: whether it
// will take a password directly, and which providers are worth offering.
export interface LoginCapabilities {
  password: boolean;
  providers: string[];
  issuer: string;
}

const DEFAULT_CAPABILITIES: LoginCapabilities = {
  password: false,
  providers: [],
  issuer: 'authz.lab0.us',
};

// fetchLoginCapabilities never rejects. An older backend has no /config route
// and an offline dev server has none at all; both mean "redirect flow only",
// which is what farstars did before embedded sign-in existed.
export async function fetchLoginCapabilities(): Promise<LoginCapabilities> {
  try {
    const res = await fetch('/api/oidc/config', { headers: { Accept: 'application/json' } });
    if (!res.ok) return DEFAULT_CAPABILITIES;
    const data = await res.json();
    return {
      password: Boolean(data.password),
      providers: Array.isArray(data.providers) ? data.providers : [],
      issuer: typeof data.issuer === 'string' && data.issuer ? data.issuer : DEFAULT_CAPABILITIES.issuer,
    };
  } catch {
    return DEFAULT_CAPABILITIES;
  }
}

// passwordLogin exchanges credentials for a session cookie via farstars' own
// backend, which relays them to authz. Resolves once the cookie is set; the
// caller re-runs fetchSession to pick the session up.
export async function passwordLogin(email: string, password: string): Promise<void> {
  const res = await fetch('/api/oidc/password-login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, return_to: '/' }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || data.error || `Sign-in failed (${res.status})`);
  }
}

// connectSim opens the authoritative game-state WebSocket (the sandbox stream by default,
// or a match's) and delivers each snapshot to onSnapshot. It reconnects with a capped
// backoff on drop, and is a silent no-op when there's no backend (offline dev) — the app
// then runs without a live economy.
export function connectSim(onSnapshot: (state: SimState) => void, path = '/api/state'): SimController {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry = 0;
  let focus = ''; // last-declared focused system, resent on (re)connect
  let nextReq = 0;
  const pending = new Map<string, (r: CommandResult) => void>();
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${window.location.host}${path}${window.location.search.includes('devUser=') ? devUserQuery(path) : ''}`;
  const sendIfOpen = (msg: object) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  };

  const open = () => {
    if (closed) return;
    ws = new WebSocket(url);
    ws.onopen = () => {
      retry = 0;
      if (focus) sendIfOpen({ type: 'focus', systemId: focus });
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as { type?: string; state?: SimState; id?: string; ok?: boolean; error?: string };
        if (msg.type === 'snapshot' && msg.state) {
          onSnapshot(msg.state);
        } else if (msg.type === 'commandResult' && msg.id) {
          pending.get(msg.id)?.({ ok: !!msg.ok, error: msg.error });
          pending.delete(msg.id);
        }
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onerror = () => ws?.close();
    ws.onclose = () => {
      ws = null;
      for (const resolve of pending.values()) resolve({ ok: false, error: 'connection lost' });
      pending.clear();
      if (closed) return;
      retry = Math.min(retry + 1, 6);
      window.setTimeout(open, 500 * retry);
    };
  };
  open();

  return {
    setSpeed(speed) {
      sendIfOpen({ type: 'setSpeed', speed });
    },
    setFocus(systemId) {
      focus = systemId ?? '';
      sendIfOpen({ type: 'focus', systemId: focus });
    },
    command(cmd) {
      const id = String(++nextReq);
      return new Promise<CommandResult>((resolve) => {
        if (!sendIfOpen({ type: 'command', id, command: cmd })) {
          resolve({ ok: false, error: 'not connected' });
          return;
        }
        pending.set(id, resolve);
      });
    },
    close() {
      closed = true;
      ws?.close();
    },
  };
}

// devUserQuery forwards a test-only ?devUser= identity (honoured only by a dev-auth
// server) onto the socket URL, so the playtest can seat several players.
function devUserQuery(path: string): string {
  const u = new URLSearchParams(window.location.search).get('devUser');
  return u ? `${path.includes('?') ? '&' : '?'}devUser=${encodeURIComponent(u)}` : '';
}

// ─── matches ───

export interface SlotView {
  empireId: string;
  name: string;
  ai: boolean;
  open: boolean;
  you: boolean;
}

export interface MatchView {
  id: string;
  name: string;
  hostName: string;
  status: 'lobby' | 'running' | 'finished';
  systems: number;
  slots: SlotView[];
  createdAt: string;
  winner?: string;
  /** Your empire id ("" when you are not seated — a spectator). */
  you: string;
  isHost: boolean;
  /** Game-seconds per wall-second at the match's "1×". */
  baseSpeed: number;
  galaxy?: Galaxy;
}

export interface NewMatch {
  name: string;
  empires: number;
  openSeats: number;
  systems?: number;
}

function withDevUser(path: string): string {
  return path + devUserQuery(path);
}

async function jsonCall<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(withDevUser(path), {
    method,
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) throw new AuthRequiredError('authentication required');
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || `request failed: ${res.status}`);
  return data as T;
}

export const listMatches = () => jsonCall<{ matches: MatchView[] }>('GET', '/api/matches').then((r) => r.matches ?? []);
export const createMatch = (m: NewMatch) => jsonCall<MatchView>('POST', '/api/matches', m);
export const getMatch = (id: string, withGalaxy = false) =>
  jsonCall<MatchView>('GET', `/api/matches/${encodeURIComponent(id)}${withGalaxy ? '?galaxy=1' : ''}`);
export const joinMatch = (id: string) => jsonCall<MatchView>('POST', `/api/matches/${encodeURIComponent(id)}/join`);
export const leaveMatch = (id: string) => jsonCall<void>('POST', `/api/matches/${encodeURIComponent(id)}/leave`);
export const startMatch = (id: string) => jsonCall<MatchView>('POST', `/api/matches/${encodeURIComponent(id)}/start`);
export const deleteMatch = (id: string) => jsonCall<void>('DELETE', `/api/matches/${encodeURIComponent(id)}`);
